import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import {
  ConfirmDialog,
  colors,
  captureException,
} from '@playbookux/playbook-web-core';

import {
  CustomIcon,
  DeviceIcon,
  DeviceIconContainer,
  Dropdown,
  ImageIcon,
  Project,
  ProjectContent,
  ProjectDescription,
  ProjectHeader,
  ProjectMenuItem,
  ProjectName,
  Typography,
  ProjectTypeContainer,
  ContainerStatus,
} from './styles';

import MoveToDialog from '../../../../../core/components/move-to-dialog';

import Plans from '../../../../../core/constants/plans';
import ParticipantsSource from '../../../../../core/constants/participantsSource';
import TestTypes from '../../../../../core/constants/testTypes';

import { deleteProjectMutation, moveProject, getProjectDependency } from './graphql';
import {
  duplicateProject,
} from '../../../../../core/services/graphql-requests';
import { dashboardProjectData } from '../../../../demo-project';

import RejectedStatus from './components/rejected-status';
import PendingApprovalStatus from './components/pending-approval-status';

const { GRAY_TEXT } = colors;

const DEMO_ID = '00000000-0000-0000-0000-000000000000';

const ProjectComponent = (props) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [showMoveTo, setShowMoveTo] = useState(false);

  const [loadingMoveTo, setLoadingMoveTo] = useState(false);
  const [errorMoveTo, setErrorMoveTo] = useState(false);
  const [projectDependency, setProjectDependency] = useState(undefined);

  const {
    project,
    onRefreshProjects,
    onSetLoadingProject,
    onRemoveDemoProject,
    company = {},
    setProjectModal,
    showModalShareProject,
    setShowModalShareProject,
    queryAttributes,
    onHandleExport,
    index,
  } = props;

  const { plan = '' } = company;

  const {
    status,
    participantsSource,
    sessionDuration,
  } = project;

  const history = useHistory();

  const inProgress = status === 'in_progress';
  const ryoParticipants = participantsSource !== ParticipantsSource.PLAYBOOK;

  const count = useMemo(() => {
    let countScheduledTesters = 0;
    let audienceParticipants = 0;
    let receivedTests = 0;

    if (project.segments) {
      project.segments.forEach((segment) => {
        countScheduledTesters += segment.countScheduledTesters;
        audienceParticipants += segment.audienceParticipants;
        receivedTests += segment.countApprovedTesters;
      });
    }

    return {
      countScheduledTesters,
      audienceParticipants,
      receivedTests,
    };
  }, [project]);

  const displayParticipantCaption = (typeProject) => {
    if (typeProject === TestTypes.MODERATED) {
      return `Participants scheduled: ${count.countScheduledTesters} of ${count.audienceParticipants}`;
    }

    return `Participants received: ${count.receivedTests} of ${count.audienceParticipants}`;
  };

  const handleDuplicateProject = async () => {
    try {
      const projectId = _.get(project, 'id');

      onSetLoadingProject(true);

      await duplicateProject(projectId, queryAttributes);
    } catch (ex) {
      captureException('HANDLE_DUPLICATE_PROJECT_FAILED', ex);
    }
    onRefreshProjects.call();
    onSetLoadingProject(false);
  };

  const handleCloseDialog = () => {
    setShowConfirm(false);
  };

  const handleOpenShareDialog = () => {
    setProjectModal(project);
    setShowModalShareProject(true);
  };

  const handleOpenDialog = () => {
    setShowConfirm(true);
  };

  const handleOpenMoveToDialog = (id) => async () => {
    setShowMoveTo(true);

    const { project: projectDependecy } = await getProjectDependency(id);
    setProjectDependency(projectDependecy);
  };

  const handleCloseMoveTo = () => {
    setShowMoveTo(false);
    setErrorMoveTo(false);
  };

  const onMoveProject = async (id, compannyId, folderId) => {
    try {
      setLoadingMoveTo(true);
      setErrorMoveTo(false);
      await moveProject(id, compannyId, folderId);
      setLoadingMoveTo(false);
      handleCloseMoveTo();
      onRefreshProjects.call();
    } catch (err) {
      setLoadingMoveTo(false);
      setErrorMoveTo(true);
    }
  };

  const handleDeleteProject = () => {
    setShowConfirm(false);
    if (project.id === dashboardProjectData.id) {
      onRemoveDemoProject();
    } else {
      deleteProjectMutation(project.id)
        .catch((ex) => {
          captureException('HANDLE_DELETE_PROJECT_FAILED', ex);
        }).finally(() => onRefreshProjects.call());
    }
  };

  const showDuplicateButton = (ryo, projectPlan) => {
    const { testType } = project;
    if (projectPlan === Plans.PAY_AS_YOU_GO
      && (ryo || testType === TestTypes.CARD_SORT || testType === TestTypes.TREE_TEST)) {
      return false;
    }
    if (testType === TestTypes.UPLOAD) {
      return false;
    }
    return true;
  };

  const removeButton = () => {
    const { testType } = project;

    if (testType === TestTypes.UPLOAD) {
      return false;
    }
    return true;
  };

  const displayProjectType = (typeProject, projectSessionDuration = '') => {
    if (typeProject === TestTypes.MODERATED) {
      return `${_.startCase(typeProject)} (${projectSessionDuration} min)`;
    }

    return _.startCase(typeProject);
  };

  const renderDeviceIcons = useMemo(() => {
    const devicesV1 = project?.segments?.map((segment) => segment.device) || [];
    const devicesV2 = project?.draftDevices || [];

    // for create project v2
    if (devicesV2 && devicesV2.length) {
      return devicesV2.map((device) => {
        const iconSelect = `/images/icon-${device}.png`;
        return <DeviceIcon key={`${device}`} src={iconSelect} alt="Device" />;
      });
    }
    // for create project v1
    return _.uniq(devicesV1).map((device) => {
      const iconSelect = `/images/icon-${device}.png`;
      return <DeviceIcon key={`${device}`} src={iconSelect} alt="Device" />;
    });
  }, [project]);

  const handleProjectClicked = () => {
    if (!showModalShareProject && !showConfirm) {
      let path;
      if (project.status !== 'draft' && project.status !== 'pending_approval' && project.status !== 'rejected') {
        path = `/projects/${project.id}/study`;
      } else if (project.testType === 'upload') {
        path = `/create-project-v2/${project.id}/upload-details`;
      } else {
        path = `/create-project-v2/${project.id}/details`;
      }
      history.push(path);
    }
  };

  const statusUppercase = _.startCase(status).toUpperCase();

  const DefaultStatus = () => <>{statusUppercase}</>;

  const ContainerStatusComponent = () => {
    switch (status) {
      case 'pending_approval':
        return <PendingApprovalStatus requestedReviewers={project?.requestedReviewers} />;
      case 'rejected':
        return <RejectedStatus rejectedBy={project?.rejectedBy} />;
      default:
        return <DefaultStatus />;
    }
  };

  return (
    <Project
      key={project.id}
      data-dashboard-project-index={index}
    >
      <ProjectContent onClick={handleProjectClicked} data-dashboard-project-name={project.name}>
        <ProjectHeader>
          <ProjectName
            text={project.name}
            lines={2}
            ellipsis="..."
            buttons={false}
          />
          <Dropdown
            data-dashboard-project-dots={index}
            item
            trigger={(
              <CustomIcon>
                <ImageIcon src="/images/dots-icon.png" alt="Playbook" />
              </CustomIcon>
            )}
          >
            <Dropdown.Menu direction="left">
              {project.status !== 'draft' && project.id !== dashboardProjectData.id && (
                <>
                  <Dropdown.Item onClick={handleOpenShareDialog} data-dashboard-project-dots-share={index}>
                    <ProjectMenuItem color={GRAY_TEXT}>
                      Share
                    </ProjectMenuItem>
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => onHandleExport(project.testType, project.id, project.name)}
                    data-dashboard-project-dots-export={index}
                  >
                    <ProjectMenuItem color={GRAY_TEXT}>
                      Export
                    </ProjectMenuItem>
                  </Dropdown.Item>
                </>
              )}

              {project.id !== dashboardProjectData.id && showDuplicateButton(ryoParticipants, plan) && (
                <Dropdown.Item
                  onClick={handleDuplicateProject}
                  data-dashboard-project-dots-duplicate={index}
                >
                  <ProjectMenuItem color={GRAY_TEXT}>
                    Duplicate
                  </ProjectMenuItem>
                </Dropdown.Item>
              )}

              {project.id !== DEMO_ID && removeButton() && (
                <Dropdown.Item
                  onClick={handleOpenMoveToDialog(project.id)}
                  data-dashboard-project-dots-move={index}
                >
                  <ProjectMenuItem color={GRAY_TEXT}>
                    Move to
                  </ProjectMenuItem>
                </Dropdown.Item>
              )}

              {(!inProgress || (inProgress && ryoParticipants)) && (
                <Dropdown.Item
                  onClick={handleOpenDialog}
                  data-dashboard-project-dots-delete={index}
                >
                  <ProjectMenuItem>
                    Delete
                  </ProjectMenuItem>
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </ProjectHeader>

        <ProjectDescription
          text={project.description}
          lines={2}
          ellipsis="..."
          buttons={false}
        />

        <Typography fontSize="15px" marginTop="5px" marginBottom="5px">
          <ContainerStatus>
            <ContainerStatusComponent />
          </ContainerStatus>
        </Typography>

        <Typography fontSize="12px">
          {`Created by ${project.createdBy.profile.firstName} ${project.createdBy.profile.lastName}`}
        </Typography>
        <Typography fontSize="12px">
          {project.launchedOn
            ? `Launched on ${moment(project.launchedOn).format('L')}`
            : `Created on ${moment(project.createdAt).format('L')}`}
        </Typography>
        {inProgress && (
          <Typography fontSize="12px">
            {displayParticipantCaption(project.testType)}
          </Typography>
        )}

        <DeviceIconContainer>
          {renderDeviceIcons}
          <ProjectTypeContainer>
            <Typography weight={500} fontSize="14px">
              {displayProjectType(project.testType, sessionDuration)}
            </Typography>
          </ProjectTypeContainer>
        </DeviceIconContainer>

      </ProjectContent>

      <ConfirmDialog
        show={showConfirm}
        title={`Do you want to delete "${project.name}"?`}
        warning="Warning: This cannot be undone"
        confirmText="Yes, delete project"
        cancelText="Cancel, keep project"
        handleConfirm={handleDeleteProject}
        handleClose={handleCloseDialog}
      />

      <MoveToDialog
        show={showMoveTo}
        handleClose={handleCloseMoveTo}
        project={project}
        onMoveProject={onMoveProject}
        loadingMoveTo={loadingMoveTo}
        projectDependency={projectDependency}
        errorMoveTo={errorMoveTo}
      />

    </Project>
  );
};

ProjectComponent.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string,
    testType: PropTypes.string,
    status: PropTypes.string,
    participantsSource: PropTypes.string,
    device: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    createdAt: PropTypes.string,
    launchedOn: PropTypes.string,
    receivedTests: PropTypes.number,
    countScheduledTesters: PropTypes.number,
    audienceParticipants: PropTypes.number,
    sessionDuration: PropTypes.number,
    draftDevices: PropTypes.arrayOf(PropTypes.string),
    createdBy: PropTypes.shape({
      profile: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
      }),
    }),
    segments: PropTypes.arrayOf(
      PropTypes.shape({
        device: PropTypes.string,
      }),
    ),
    rejectedBy: PropTypes.arrayOf(),
    requestedReviewers: PropTypes.arrayOf(),
  }),
  onRefreshProjects: PropTypes.func.isRequired,
  onSetLoadingProject: PropTypes.func.isRequired,
  onRemoveDemoProject: PropTypes.func.isRequired,
  company: PropTypes.shape({
    plan: PropTypes.string,
  }).isRequired,
  setProjectModal: PropTypes.func.isRequired,
  showModalShareProject: PropTypes.bool.isRequired,
  setShowModalShareProject: PropTypes.func.isRequired,
  queryAttributes: PropTypes.shape({
    variables: PropTypes.shape({
      limit: PropTypes.number,
      offset: PropTypes.number,
      companyId: PropTypes.string,
      name: PropTypes.string,
      description: PropTypes.string,
      status: PropTypes.string,
      folderIds: PropTypes.arrayOf(PropTypes.string),
      order: PropTypes.string,
    }),
    fetchPolicy: PropTypes.string,
  }).isRequired,
  onHandleExport: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};

ProjectComponent.defaultProps = {
  project: {},
};

export default ProjectComponent;
